<template>
  <div class="audit-log-container">
    <div class="audit-header">
      <div class="header-content">
        <div class="header-icon-circle">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
        </div>
        <div>
          <h2>Tevékenységnapló (Audit Log)</h2>
          <p>Fizetési műveletek, táncóra dátumok és csoportbeosztások audit naplója</p>
        </div>
      </div>
      <button class="btn-refresh" @click="fetchLogs" :disabled="loading" title="Frissítés">
        <svg :class="{ 'spinning': loading }" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23,4 23,10 17,10"/>
          <polyline points="1,20 1,14 7,14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        <span>Frissítés</span>
      </button>
    </div>

    <!-- Quick Stats Cards -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-icon stat-icon--total">📋</div>
        <div class="stat-info">
          <span class="stat-value">{{ totalCount }}</span>
          <span class="stat-label">Összes rögzített esemény</span>
        </div>
      </div>
      <div class="stat-card" @click="filterByCategory('payment')">
        <div class="stat-icon stat-icon--payment">💳</div>
        <div class="stat-info">
          <span class="stat-value">{{ paymentCount }}</span>
          <span class="stat-label">Fizetési esemény</span>
        </div>
      </div>
      <div class="stat-card" @click="filterByCategory('lesson_date')">
        <div class="stat-icon stat-icon--lesson">📅</div>
        <div class="stat-info">
          <span class="stat-value">{{ lessonDateCount }}</span>
          <span class="stat-label">Óra / Dátum változás</span>
        </div>
      </div>
      <div class="stat-card" @click="filterByCategory('group_assignment')">
        <div class="stat-icon stat-icon--group">👥</div>
        <div class="stat-info">
          <span class="stat-value">{{ groupCount }}</span>
          <span class="stat-label">Csoportbeosztás</span>
        </div>
      </div>
    </div>

    <!-- Search and Filters Bar -->
    <div class="controls-card">
      <div class="search-box">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input 
          type="text" 
          v-model="searchQuery" 
          @input="debounceSearch"
          placeholder="Keresés név, felhasználó vagy művelet alapján..." 
        />
        <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''; fetchLogs()">✕</button>
      </div>

      <!-- Category Filter Pills -->
      <div class="category-pills">
        <button 
          v-for="cat in categories" 
          :key="cat.key" 
          class="pill-btn" 
          :class="{ active: selectedCategory === cat.key }"
          @click="selectCategory(cat.key)"
        >
          <span class="pill-icon">{{ cat.icon }}</span>
          <span>{{ cat.label }}</span>
        </button>
      </div>
    </div>

    <!-- Log List -->
    <div class="logs-card">
      <div v-if="loading && logs.length === 0" class="loading-state">
        <div class="spinner"></div>
        <p>Napló betöltése...</p>
      </div>

      <div v-else-if="logs.length === 0" class="empty-state">
        <div class="empty-icon">📂</div>
        <h3>Nincs megjeleníthető naplóbejegyzés</h3>
        <p>A megadott szűrési feltételekkel nem található tevékenység.</p>
      </div>

      <div v-else class="log-timeline">
        <div 
          v-for="log in logs" 
          :key="log.id" 
          class="log-entry"
          :class="'log-entry--' + (log.category || 'default')"
        >
          <div class="log-entry__left">
            <div class="category-badge-circle" :title="getCategoryLabel(log.category)">
              {{ getCategoryIcon(log.category) }}
            </div>
            <div class="timeline-line"></div>
          </div>

          <div class="log-entry__body">
            <div class="log-entry__header">
              <div class="actor-info">
                <span class="actor-name">{{ log.user_name || 'Rendszer' }}</span>
                <span class="role-badge" :class="'role-badge--' + log.user_role">
                  {{ formatRole(log.user_role) }}
                </span>
              </div>
              <div class="log-time" :title="log.created_at">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                <span>{{ log.created_at }}</span>
              </div>
            </div>

            <div class="log-entry__details">
              <span class="action-tag" :class="'action-tag--' + log.category">
                {{ formatAction(log.action) }}
              </span>
              <span class="details-text">{{ log.details }}</span>
            </div>

            <div class="log-entry__meta" v-if="log.target_name || log.metadata">
              <div v-if="log.target_name" class="target-chip">
                <span class="target-label">{{ formatTargetType(log.target_type) }}:</span>
                <strong class="target-val">{{ log.target_name }}</strong>
              </div>

              <!-- Extra Meta Tags -->
              <div v-if="log.metadata && log.metadata.amount" class="meta-badge meta-badge--money">
                Összeg: {{ formatPrice(log.metadata.amount) }}
              </div>
              <div v-if="log.metadata && log.metadata.method" class="meta-badge">
                Mód: {{ log.metadata.method }}
              </div>
              <div v-if="log.metadata && log.metadata.balance_after !== undefined" class="meta-badge">
                Új egyenleg: {{ formatPrice(log.metadata.balance_after) }}
              </div>
              <div v-if="log.ip_address" class="meta-badge meta-badge--ip" title="IP cím">
                🌐 {{ log.ip_address }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="totalCount > limit">
        <button 
          class="page-btn" 
          :disabled="offset === 0 || loading"
          @click="prevPage"
        >
          &larr; Előző
        </button>
        <span class="page-info">
          {{ offset + 1 }} - {{ Math.min(offset + limit, totalCount) }} / {{ totalCount }} bejegyzés
        </span>
        <button 
          class="page-btn" 
          :disabled="offset + limit >= totalCount || loading"
          @click="nextPage"
        >
          Következő &rarr;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AuditLogSettingsItem',
  data() {
    return {
      logs: [],
      totalCount: 0,
      loading: false,
      limit: 40,
      offset: 0,
      searchQuery: '',
      selectedCategory: 'all',
      searchTimeout: null,
      categories: [
        { key: 'all', label: 'Összes', icon: '⚡' },
        { key: 'payment', label: 'Fizetések', icon: '💳' },
        { key: 'lesson_date', label: 'Órák & Dátumok', icon: '📅' },
        { key: 'group_assignment', label: 'Csoportbeosztás', icon: '👥' },
        { key: 'pricing', label: 'Árak & Kedvezmények', icon: '🏷️' },
        { key: 'dancer', label: 'Táncosok', icon: '👤' }
      ]
    };
  },
  computed: {
    paymentCount() {
      return this.logs.filter(l => l.category === 'payment').length;
    },
    lessonDateCount() {
      return this.logs.filter(l => l.category === 'lesson_date').length;
    },
    groupCount() {
      return this.logs.filter(l => l.category === 'group_assignment').length;
    }
  },
  mounted() {
    this.fetchLogs();
  },
  methods: {
    api() {
      return this.$BACKEND_HOST + '/api';
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          limit: this.limit,
          offset: this.offset
        };
        if (this.selectedCategory && this.selectedCategory !== 'all') {
          params.category = this.selectedCategory;
        }
        if (this.searchQuery && this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
        }
        const res = await axios.get(`${this.api()}/audit-logs`, { params });
        this.logs = res.data.logs || [];
        this.totalCount = res.data.total || 0;
      } catch (e) {
        console.error('Error fetching audit logs:', e);
      } finally {
        this.loading = false;
      }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.offset = 0;
        this.fetchLogs();
      }, 300);
    },
    selectCategory(cat) {
      this.selectedCategory = cat;
      this.offset = 0;
      this.fetchLogs();
    },
    filterByCategory(cat) {
      this.selectCategory(cat);
    },
    nextPage() {
      if (this.offset + this.limit < this.totalCount) {
        this.offset += this.limit;
        this.fetchLogs();
      }
    },
    prevPage() {
      if (this.offset > 0) {
        this.offset = Math.max(0, this.offset - this.limit);
        this.fetchLogs();
      }
    },
    formatPrice(price) {
      if (price === undefined || price === null || isNaN(price)) return '0 Ft';
      return Math.round(Number(price)).toLocaleString('hu-HU') + ' Ft';
    },
    formatRole(role) {
      const map = {
        admin: 'Admin',
        teacher: 'Tanár',
        assistant: 'Asszisztens',
        dancer: 'Táncos',
        parent: 'Szülő'
      };
      return map[role] || role || 'Felhasználó';
    },
    getCategoryIcon(cat) {
      const map = {
        payment: '💳',
        lesson_date: '📅',
        group_assignment: '👥',
        pricing: '🏷️',
        dancer: '👤',
        system: '⚙️'
      };
      return map[cat] || '📋';
    },
    getCategoryLabel(cat) {
      const map = {
        payment: 'Fizetés',
        lesson_date: 'Óra / Dátum',
        group_assignment: 'Csoportbeosztás',
        pricing: 'Árak és kedvezmények',
        dancer: 'Táncos',
        system: 'Rendszer'
      };
      return map[cat] || cat;
    },
    formatAction(action) {
      const map = {
        wallet_topup: 'Befizetés',
        wallet_correct: 'Egyenleg korrekció',
        wallet_refund: 'Visszatérítés',
        set_override: 'Egyedi díj beállítás',
        group_price_update: 'Alapár módosítás',
        discount_rule_create: 'Új kedvezmény',
        discount_rule_update: 'Kedvezmény szerkesztés',
        discount_rule_delete: 'Kedvezmény törlés',
        group_create: 'Csoport létrehozás',
        group_name_update: 'Csoport átnevezés',
        lesson_schedule_update: 'Órarend módosítás',
        group_delete: 'Csoport törlés',
        lesson_cancel: 'Óra lemondás',
        lesson_complete: 'Óra lezárás',
        dancer_add_group: 'Csoporthoz adás',
        dancer_remove_group: 'Csoportból törlés',
        dancer_assign_groups: 'Csoportbeosztás',
        dancer_set_primary_group: 'Főcsoport váltás',
        dancer_update_group: 'Csoportváltás',
        dancer_status_update: 'Státuszváltás',
        dancer_delete: 'Táncos törlés',
        competition_create: 'Új verseny',
        competition_delete: 'Verseny törlés',
        calendar_event_create: 'Új esemény',
        calendar_event_delete: 'Esemény törlés'
      };
      return map[action] || action;
    },
    formatTargetType(targetType) {
      const map = {
        child: 'Táncos',
        group: 'Csoport',
        event: 'Táncóra',
        competition: 'Verseny',
        calendar_event: 'Naptári esemény',
        discount_rule: 'Szabály'
      };
      return map[targetType] || targetType || 'Célpont';
    }
  }
};
</script>

<style scoped>
.audit-log-container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.audit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon-circle {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.15), rgba(99, 102, 241, 0.08));
  border: 1px solid rgba(79, 70, 229, 0.25);
  color: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.audit-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-heading);
}

.audit-header p {
  margin: 0.2rem 0 0;
  font-size: 0.9rem;
  color: var(--color-text-muted);
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 10px;
  color: var(--color-text);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: var(--color-background-soft, #f8fafc);
  border-color: #4f46e5;
  color: #4f46e5;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.85rem;
}

.stat-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-color: #4f46e5;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  background: var(--color-background-soft, #f1f5f9);
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--color-heading);
  line-height: 1.2;
}

.stat-label {
  font-size: 0.78rem;
  color: var(--color-text-muted);
  font-weight: 600;
  margin-top: 0.15rem;
}

/* Controls Card */
.controls-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  padding: 1rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: var(--color-background-soft, #f8fafc);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 10px;
  padding: 0.5rem 0.85rem;
  color: var(--color-text-muted);
}

.search-box input {
  border: none;
  background: transparent;
  width: 100%;
  font-size: 0.92rem;
  color: var(--color-text);
  outline: none;
}

.clear-btn {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 0.9rem;
}

.category-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pill-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  border-radius: 9999px;
  border: 1px solid var(--color-border, #e2e8f0);
  background: var(--card-bg, #ffffff);
  color: var(--color-text);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pill-btn:hover {
  background: var(--color-background-soft, #f1f5f9);
}

.pill-btn.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
}

/* Logs Card & Timeline */
.logs-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  padding: 1.5rem;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-muted);
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(79, 70, 229, 0.2);
  border-top-color: #4f46e5;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 0.8s linear infinite;
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.log-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.log-entry {
  display: flex;
  gap: 1rem;
  position: relative;
}

.log-entry__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.category-badge-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--color-background-soft, #f1f5f9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  border: 2px solid var(--card-bg, #ffffff);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  z-index: 2;
}

.timeline-line {
  flex-grow: 1;
  width: 2px;
  background: var(--color-border, #e2e8f0);
  margin: 0.3rem 0;
}

.log-entry:last-child .timeline-line {
  display: none;
}

.log-entry__body {
  flex-grow: 1;
  padding-bottom: 1.5rem;
}

.log-entry__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
}

.actor-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.actor-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--color-heading);
}

.role-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.role-badge--admin {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.role-badge--teacher {
  background: rgba(79, 70, 229, 0.12);
  color: #4f46e5;
  border: 1px solid rgba(79, 70, 229, 0.25);
}

.role-badge--assistant {
  background: rgba(14, 165, 233, 0.12);
  color: #0284c7;
  border: 1px solid rgba(14, 165, 233, 0.25);
}

.log-time {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--color-text-muted);
}

.log-entry__details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.action-tag {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
}

.action-tag--payment {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.action-tag--lesson_date {
  background: rgba(147, 51, 234, 0.12);
  color: #7e22ce;
}

.action-tag--group_assignment {
  background: rgba(14, 165, 233, 0.12);
  color: #0284c7;
}

.action-tag--pricing {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.action-tag--dancer {
  background: rgba(236, 72, 153, 0.12);
  color: #db2777;
}

.details-text {
  font-size: 0.92rem;
  color: var(--color-text);
  font-weight: 500;
}

.log-entry__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.78rem;
}

.target-chip {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--color-background-soft, #f8fafc);
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  border: 1px solid var(--color-border, #e2e8f0);
}

.target-label {
  color: var(--color-text-muted);
}

.target-val {
  color: var(--color-heading);
}

.meta-badge {
  background: var(--color-background-soft, #f8fafc);
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  border: 1px solid var(--color-border, #e2e8f0);
  color: var(--color-text-muted);
  font-weight: 600;
}

.meta-badge--money {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.25);
  color: #059669;
}

.meta-badge--ip {
  font-size: 0.72rem;
}

/* Pagination */
.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.2rem;
  border-top: 1px solid var(--color-border, #e2e8f0);
  margin-top: 0.5rem;
}

.page-btn {
  padding: 0.45rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border, #e2e8f0);
  background: var(--card-bg, #ffffff);
  color: var(--color-text);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background: var(--color-background-soft, #f1f5f9);
  border-color: #4f46e5;
  color: #4f46e5;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.85rem;
  color: var(--color-text-muted);
  font-weight: 500;
}
</style>
